import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Download, Loader2, Lock, PenLine, Save, Unlock } from 'lucide-react';
import { OasisSignModal } from '../components/OasisSignModal';
import { chooseFillMode } from '../utils/fillable';
import { toast } from 'sonner';
import { buildOasisPdf, fromGenerated } from '../utils/oasisPdf';
import DashboardLayout from '../components/DashboardLayout';
import api from '../api';
import { OasisItem, isAnswered } from '../components/OasisE2Item';

const usDate = (v) => (/^\d{4}-\d{2}-\d{2}/.test(v || '') ? `${v.slice(5, 7)}/${v.slice(8, 10)}/${v.slice(0, 4)}` : v);
const TP = { SOC: 'Start of Care', ROC: 'Resumption of Care', FU: 'Follow-up (Recertification)', TRN: 'Transfer to an Inpatient Facility', DC: 'Discharge from Agency', DAH: 'Death at Home' };

export default function OasisE2AssessmentPage({ defaultTp = 'SOC' }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const formId = params.get('id');
  const appointmentId = params.get('appointmentId');
  const [tp, setTp] = useState(params.get('tp') || defaultTp);
  const [patients, setPatients] = useState([]);
  const [patientId, setPatientId] = useState(params.get('patientId') || '');
  const [items, setItems] = useState([]);
  const [itemsTp, setItemsTp] = useState('');
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [legacy, setLegacy] = useState(false);
  const [record, setRecord] = useState(null);
  const [showSign, setShowSign] = useState(false);
  const [signing, setSigning] = useState(false);
  const locked = status === 'Locked';
  const [filling, setFilling] = useState(false);
  const autofillFor = useRef('');
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  useEffect(() => { api.get('/patients').then(r => setPatients(r.data.patients || [])).catch(() => {}); }, []);
  useEffect(() => { api.get(`/oasis-e2/catalog/${tp}`).then(r => { setItems(r.data.items || []); setItemsTp(tp); }).catch(() => toast.error('Could not load OASIS-E2 items')); }, [tp]);
  useEffect(() => {
    if (!formId) return;
    api.get(`/forms/oasis-e2/${formId}`).then(r => {
      const a = r.data.assessment;
      setTp(a.timepoint); setPatientId(a.patientId); setAnswers(a.answers || {}); setStatus(a.status); setRecord(a);
    }).catch(() => setLegacy(true));
  }, [formId]);
  useEffect(() => {
    if (formId || !patientId) return;
    api.get(`/oasis-e2/prefill/${tp}/${patientId}`).then(async r => {
      let pre = r.data.answers || {};
      if (appointmentId) {
        const v = await api.get(`/recert/visit/${appointmentId}`).catch(() => null);
        if (v?.data?.prefill?.visitDate) pre = { ...pre, M0090: v.data.prefill.visitDate };
      }
      setAnswers(prev => ({ ...pre, ...prev }));
    }).catch(() => {});
  }, [patientId, tp, formId, appointmentId]);
  // New assessment: open the patient's draft, or build one from the chart, last OASIS and uploads
  useEffect(() => {
    if (formId || !patientId || sessionStorage.getItem('oasisE2Prefill')) return undefined;
    const key = `${patientId}|${tp}|${appointmentId || ''}`;
    if (autofillFor.current === key) return undefined;
    autofillFor.current = key;
    const open = (id, msg) => { setFilling(false); if (msg) toast.success(msg); navigate(`${window.location.pathname}?id=${id}`, { replace: true }); };
    (async () => {
      try {
        const r = await api.post(`/oasis-e2/autofill/${tp}/${patientId}${appointmentId ? `?appointmentId=${appointmentId}` : ''}`);
        if (r.data.existingId) {
          if (r.data.chartOnly) toast.warning(`This ${tp} only has the patient details: the chart has no diagnosis, medications, earlier OASIS or uploaded papers yet. Upload the referral or H&P with Upload & Auto-Populate to fill the rest.`, { duration: 12000 });
          open(r.data.existingId, `Opened this patient's ${tp} draft`); return;
        }
        setFilling(true);
        for (let i = 0; i < 90 && mounted.current; i++) {
          await new Promise(res => setTimeout(res, 2000));
          const j = (await api.get(`/oasis-e2/autofill-jobs/${r.data.jobId}`)).data.job;
          if (j.status === 'completed') {
            if (j.chartOnly) toast.warning(`Only the patient details could be filled: this chart has no diagnosis, medications, earlier OASIS or uploaded papers yet. Upload the referral or H&P with Upload & Auto-Populate and reopen this ${tp} to fill the rest.`, { duration: 12000 });
            open(j.formId, !j.chartOnly ? `Pre-filled ${j.filled} items from ${j.sources.join(', ')}. ${j.missing} left for the clinician.` : '');
            return;
          }
          if (j.status === 'failed') { toast.error('Auto-fill failed; the chart details are filled in, please complete the rest.'); break; }
        }
      } catch { /* keep the basic chart prefill */ }
      if (mounted.current) setFilling(false);
    })();
    return undefined;
  }, [patientId, tp, formId, appointmentId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (formId || patientId || !appointmentId) return;
    api.get(`/recert/visit/${appointmentId}`).then(r => setPatientId(r.data.assignment.patientId)).catch(() => {});
  }, [appointmentId, formId, patientId]);

  useEffect(() => {
    const raw = sessionStorage.getItem('oasisE2Prefill');
    if (formId || !raw || !items.length || itemsTp !== tp) return;
    const data = JSON.parse(raw);
    const m0100 = String(Object.entries(data).find(([k]) => k.startsWith('M0100'))?.[1] || '').trim()[0];
    const want = { 1: 'SOC', 3: 'ROC', 4: 'FU', 5: 'FU', 6: 'TRN', 7: 'TRN', 8: 'DAH', 9: 'DC' }[m0100];
    if (want && want !== tp && !appointmentId) { setTp(want); return; }
    sessionStorage.removeItem('oasisE2Prefill');
    const gen = fromGenerated(items, data);
    setAnswers(prev => ({ ...prev, ...gen }));
    if (Object.keys(gen).length) toast.success(`Filled ${Object.keys(gen).length} OASIS-E2 items from the AI Document Generator. Review every item before saving.`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, itemsTp, formId, tp]);

  const sections = useMemo(() => [...new Set(items.map(i => i.section))], [items]);
  const missing = items.filter(it => !isAnswered(it, answers[it.code]));
  const patient = patients.find(p => p.id === patientId);
  const patientName = patient ? `${patient.firstName} ${patient.lastName}` : (answers.M0040 || '');

  const save = async (quiet = false) => {
    if (!patientId) { toast.error('Select a patient first'); return null; }
    setSaving(true);
    try {
      const body = { patientId, timepoint: tp, answers, appointmentId: appointmentId || undefined };
      const r = formId ? await api.put(`/forms/oasis-e2/${formId}`, body) : await api.post('/forms/oasis-e2', body);
      const a = r.data.assessment;
      setStatus(a.status); setRecord(r2 => ({ ...r2, ...a }));
      if (quiet) return a;
      toast.success(a.missing.length ? `Saved. ${a.missing.length} item(s) still unanswered.` : 'Saved. All OASIS-E2 items answered.');
      if (!formId) navigate(`?id=${a.id}${appointmentId ? `&appointmentId=${appointmentId}` : ''}`, { replace: true });
      return a;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const sign = async (body) => {
    setSigning(true);
    try {
      const saved = await save(true);
      if (!saved) return;
      if (saved.missing.length) { toast.error(`Answer all items before signing (${saved.missing.length} left)`); return; }
      const r = await api.post(`/forms/oasis-e2/${formId}/sign`, body);
      setRecord(x => ({ ...x, ...r.data.assessment })); setStatus('Locked'); setShowSign(false);
      toast.success('Assessment signed and locked');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not sign');
    } finally {
      setSigning(false);
    }
  };

  const unlock = async () => {
    const reason = window.prompt('Reason for unlocking this signed assessment (kept on record):');
    if (reason === null) return;
    try {
      const r = await api.post(`/forms/oasis-e2/${formId}/unlock`, { reason });
      setRecord(x => ({ ...x, ...r.data.assessment })); setStatus(r.data.assessment.status);
      toast.success(`Unlocked. The previous signature was removed; sign again after corrections.${r.data.notified ? ` ${r.data.notified} was emailed the reason.` : ''}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not unlock');
    }
  };

  const signedBanner = locked && record?.signature && (
    <div className="flex flex-wrap items-center justify-between gap-3 bg-green-50 border border-green-300 rounded-lg p-3" data-testid="oasis-e2-signed-banner">
      <div className="flex items-center gap-3">
        <Lock className="w-5 h-5 text-green-700" />
        <img src={record.signature.image} alt="Clinician signature" className="h-10 bg-white border rounded" />
        <p className="text-sm">Signed by <strong>{record.signature.name}{record.signature.credentials ? `, ${record.signature.credentials}` : ''}</strong> on {new Date(record.signature.signedAt).toLocaleString()}. Locked.</p>
      </div>
      {record.canUnlock && <button onClick={unlock} className="px-3 py-1.5 text-sm rounded border border-gray-400 hover:bg-white flex items-center gap-1" data-testid="oasis-e2-unlock"><Unlock className="w-4 h-4" /> Unlock to correct</button>}
    </div>
  );

  if (legacy) {
    return (
      <DashboardLayout>
        <div className="p-6" data-testid="oasis-legacy-notice">
          <p className="mb-3">This assessment was saved with the older OASIS-E form.</p>
          <button onClick={() => navigate(`/dashboard/forms/recertification-legacy${window.location.search}`)} className="px-4 py-2 rounded bg-gray-900 text-[#d4af37]" data-testid="oasis-open-legacy">Open the saved OASIS-E form</button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 space-y-4" data-testid="oasis-e2-page">
        {filling && (
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-900 flex items-center gap-2" data-testid="oasis-autofill-banner">
            <Loader2 className="w-4 h-4 animate-spin" /> Pre-filling this {tp} from the patient's chart, last OASIS and uploaded documents. This takes about a minute…
          </div>
        )}
        <div className="bg-gray-900 text-white rounded-lg p-4 flex flex-wrap items-center gap-3 justify-between sticky top-0 z-20">
          <div>
            <h1 className="text-xl font-bold text-[#d4af37]" data-testid="oasis-e2-title">OASIS-E2 {TP[tp]} ({tp})</h1>
            <p className="text-xs text-white/70">CMS instrument effective 04/01/2026, OMB No. 0938-1279. Items, wording and response codes follow the official time point instrument and the OASIS-E2 Guidance Manual.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-xs px-2 py-1 rounded-full ${missing.length ? 'bg-amber-500 text-gray-900' : 'bg-green-600'}`} data-testid="oasis-e2-progress">
              {items.length - missing.length}/{items.length} answered{status ? `, ${status}` : ''}
            </span>
            <button onClick={() => navigate('/dashboard/forms/oasis-e2/sign-list')} className="px-3 py-1.5 rounded bg-white/10 hover:bg-white/20 text-sm" data-testid="oasis-e2-sign-list">Sign list</button>
            <button onClick={async () => {
              const mode = await chooseFillMode();
              if (!mode) return;
              const blank = mode === 'blank';
              const doc = buildOasisPdf(tp, items, blank ? {} : answers, blank ? {} : { signature: locked ? record?.signature : null, clinicianName: record?.signature?.name, patientName, dateOfBirth: answers.M0066 || usDate(patient?.dateOfBirth), mrn: answers.M0020 || patient?.medicalRecordNumber || '', assessmentDate: answers.M0090 });
              doc.fillMode = mode;
              doc.save(`OASIS-E2_${tp}_${blank ? 'blank' : (patientName || 'patient').replace(/\s+/g, '_')}.pdf`);
            }} className="px-3 py-1.5 rounded bg-white/10 hover:bg-white/20 text-sm flex items-center gap-1" data-testid="oasis-e2-pdf"><Download className="w-4 h-4" /> PDF</button>
            {formId && !locked && (
              <button onClick={() => (missing.length ? toast.error(`Answer all items before signing (${missing.length} left)`) : setShowSign(true))}
                className="px-3 py-1.5 rounded bg-white text-gray-900 text-sm font-semibold flex items-center gap-1" data-testid="oasis-e2-sign"><PenLine className="w-4 h-4" /> Sign</button>
            )}
            <button onClick={() => save()} disabled={saving || locked} className="px-4 py-1.5 rounded bg-[#d4af37] text-gray-900 font-semibold text-sm flex items-center gap-1 disabled:opacity-50" data-testid="oasis-e2-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
            </button>
          </div>
        </div>

        {signedBanner}
        {patient && (
          <div className="flex flex-wrap gap-x-6 gap-y-1 bg-amber-50 border border-[#d4af37] rounded-lg px-4 py-2 text-sm" data-testid="oasis-e2-patient-strip">
            {[['Patient', patientName], ['DOB', usDate(patient.dateOfBirth)], ['MRN', patient.medicalRecordNumber], ['Dx', patient.primaryDiagnosis],
              ['Admission', usDate(patient.startOfCareDate || patient.admitDate || patient.admissionDate)]].map(([k, v]) => (
              <span key={k} data-testid={`oasis-e2-strip-${k.toLowerCase()}`}><span className="text-gray-500">{k}:</span> <strong>{v || '—'}</strong></span>
            ))}
          </div>
        )}
        {showSign && <OasisSignModal onSign={sign} onClose={() => setShowSign(false)} signing={signing} />}
        <div className="flex flex-wrap gap-3 bg-white border rounded-lg p-3">
          <select value={patientId} onChange={(e) => setPatientId(e.target.value)} disabled={Boolean(formId)} className="border rounded px-3 py-2 text-sm min-w-[16rem]" data-testid="oasis-e2-patient">
            <option value="">Select patient</option>
            {patients.map(p => <option key={p.id} value={p.id}>{p.lastName}, {p.firstName}</option>)}
          </select>
          <select value={tp} onChange={(e) => setTp(e.target.value)} disabled={Boolean(formId) || Boolean(appointmentId)} className="border rounded px-3 py-2 text-sm" data-testid="oasis-e2-timepoint">
            {Object.entries(TP).map(([k, l]) => <option key={k} value={k}>{k} - {l}</option>)}
          </select>
          {missing.length > 0 && (
            <button onClick={() => document.getElementById(`oasis-item-${missing[0].code}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
              className="text-sm text-amber-700 hover:underline" data-testid="oasis-e2-next-missing">Go to next unanswered ({missing[0].code})</button>
          )}
          {!missing.length && items.length > 0 && <span className="text-sm text-green-700 flex items-center gap-1" data-testid="oasis-e2-status-chip">{locked ? <Lock className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />} {locked ? 'Signed and locked' : 'Complete'}</span>}
        </div>

        {sections.map(sec => (
          <section key={sec} className="space-y-3" data-testid={`oasis-section-${sec}`}>
            <h2 className="text-lg font-bold text-gray-800 border-b-2 border-[#d4af37] pb-1">{sec}</h2>
            {items.filter(i => i.section === sec).map(it => (
              <OasisItem key={it.code} it={it} locked={locked} value={answers[it.code]} onChange={(v) => setAnswers(a => ({ ...a, [it.code]: v }))} />
            ))}
          </section>
        ))}
      </div>
    </DashboardLayout>
  );
}
